// admin/src/pages/Notifications/Notifications.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { Colors } from "../constants/Colors";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import "../styles/notifications.css";

const MySwal = withReactContent(Swal);
const PAGE_SIZE = 20;
const PANEL_REFRESH_MS = 30000; // auto-refresh panel while open

/* ────────────────────────────────────────────────────────────────
   💡 Toggle this to `true` once the parent component handles
   onOpenModal(true, "payment", ...) and onOpenModal(true, "passenger", ...)
   with proper modals. While `false`, we use SweetAlert display.
   ──────────────────────────────────────────────────────────────── */
const USE_PARENT_MODALS = {
  payment: false,
  passenger: false,
  operator: false,
};

/* Filter tabs */
const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "rental", label: "Rentals" },
  { key: "passenger", label: "Passengers" },
  { key: "payment", label: "Payments" },
  { key: "system", label: "System" },
];

/* Currency formatter */
const fmtCurrency = (n) =>
  "₱" +
  Number(n || 0).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* Escape HTML for safety in SweetAlert HTML strings */
const escapeHtml = (str) => {
  if (str == null) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const Notifications = ({
  isOpen,
  onClose,
  onOpenModal,
  onUnreadCountChange,
}) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [actingOnId, setActingOnId] = useState(null);

  const { admin } = useAuth();
  const notificationRef = useRef(null);
  const loadingRef = useRef(false);

  /* ── Fetch a page of notifications ── */
  const fetchNotifications = useCallback(
    async (pageNum, reset = false) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const params = {
          page: pageNum,
          limit: PAGE_SIZE,
          unread_only: filter === "unread",
        };

        if (
          filter === "rental" ||
          filter === "system" ||
          filter === "passenger" ||
          filter === "payment"
        ) {
          params.type = filter;
        }

        const response = await api.get("/admin/notifications", { params });

        if (response.data.success) {
          const newNotifications = response.data.data || [];

          setNotifications((prev) =>
            reset ? newNotifications : [...prev, ...newNotifications],
          );
          setUnreadCount(response.data.unread_count || 0);
          setHasMore(newNotifications.length === PAGE_SIZE);
        } else {
          throw new Error(
            response.data.message || "Failed to fetch notifications",
          );
        }
      } catch (err) {
        console.error("Error fetching notifications:", err);
        setError(err.response?.data?.message || err.message);
        MySwal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load notifications. Please try again.",
          confirmButtonColor: Colors.PRIMARY,
        });
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [filter],
  );

  /* ── Mark one as read ── */
  const markAsRead = async (notificationId) => {
    try {
      const response = await api.put(
        `/admin/notifications/${notificationId}/read`,
      );
      if (response.data.success) {
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === notificationId
              ? { ...notif, is_read: true, read_at: new Date().toISOString() }
              : notif,
          ),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  /* ── Mark all as read ── */
  const markAllAsRead = async () => {
    try {
      const result = await MySwal.fire({
        title: "Mark All as Read?",
        text: "This will mark all notifications as read.",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: Colors.PRIMARY,
        cancelButtonColor: Colors.GRAY,
        confirmButtonText: "Yes, mark all",
      });

      if (result.isConfirmed) {
        const response = await api.put("/admin/notifications/mark-all-read");
        if (response.data.success) {
          setNotifications((prev) =>
            prev.map((notif) => ({
              ...notif,
              is_read: true,
              read_at: new Date().toISOString(),
            })),
          );
          setUnreadCount(0);
          MySwal.fire({
            icon: "success",
            title: "Success",
            text: "All notifications marked as read",
            timer: 1500,
            showConfirmButton: false,
          });
        }
      }
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to mark notifications as read",
        confirmButtonColor: Colors.PRIMARY,
      });
    }
  };

  /* ── Delete ── */
  const deleteNotification = async (notificationId) => {
    try {
      const result = await MySwal.fire({
        title: "Delete Notification?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: Colors.RED,
        cancelButtonColor: Colors.GRAY,
        confirmButtonText: "Yes, delete it",
      });

      if (result.isConfirmed) {
        const response = await api.delete(
          `/admin/notifications/${notificationId}`,
        );
        if (response.data.success) {
          const deletedNotif = notifications.find(
            (n) => n.id === notificationId,
          );
          setNotifications((prev) =>
            prev.filter((notif) => notif.id !== notificationId),
          );
          if (deletedNotif && !deletedNotif.is_read) {
            setUnreadCount((prev) => Math.max(0, prev - 1));
          }
          MySwal.fire({
            icon: "success",
            title: "Deleted",
            text: "Notification has been deleted",
            timer: 1500,
            showConfirmButton: false,
          });
        }
      }
    } catch (err) {
      console.error("Error deleting notification:", err);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete notification",
        confirmButtonColor: Colors.PRIMARY,
      });
    }
  };

  /* ── Accept / reject a rental request ── */
  const respondToRental = async (notification, decision) => {
    const rentalId = notification.data?.rental_id;
    if (!rentalId) return;

    setActingOnId(notification.id);
    try {
      const response = await api.put(`/admin/rentals/${rentalId}/${decision}`);
      if (response.data.success) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id
              ? {
                  ...n,
                  is_read: true,
                  read_at: n.read_at || new Date().toISOString(),
                  data: { ...n.data, action_required: false, decision },
                }
              : n,
          ),
        );
        if (!notification.is_read) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
        MySwal.fire({
          icon: "success",
          title: decision === "accept" ? "Rental Accepted" : "Rental Rejected",
          timer: 1400,
          showConfirmButton: false,
        });
      } else {
        throw new Error(response.data.message || "Request failed");
      }
    } catch (err) {
      console.error(`Error ${decision}ing rental:`, err);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message ||
          `Failed to ${decision} the rental request. Try again.`,
        confirmButtonColor: Colors.PRIMARY,
      });
    } finally {
      setActingOnId(null);
    }
  };

  /* ── Show payment detail (SweetAlert fallback) ── */
  const showPaymentDetail = (notification) => {
    const d = notification.data || {};

    MySwal.fire({
      title: escapeHtml(notification.title || "Payment"),
      html: `
        <div style="text-align:left; font-size:14px; line-height:1.7;">
          <div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
            <span style="color:#6B7280;">Amount</span>
            <strong>${fmtCurrency(d.amount)}</strong>
          </div>
          ${
            d.commission_amount != null
              ? `<div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
                   <span style="color:#6B7280;">Commission</span>
                   <strong>${fmtCurrency(d.commission_amount)}</strong>
                 </div>`
              : ""
          }
          ${
            d.operator_earnings != null
              ? `<div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
                   <span style="color:#6B7280;">Operator Net</span>
                   <strong>${fmtCurrency(d.operator_earnings)}</strong>
                 </div>`
              : ""
          }
          ${
            d.passenger_name
              ? `<div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
                   <span style="color:#6B7280;">Passenger</span>
                   <strong>${escapeHtml(d.passenger_name)}</strong>
                 </div>`
              : ""
          }
          ${
            d.payment_method
              ? `<div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
                   <span style="color:#6B7280;">Method</span>
                   <strong>${escapeHtml(String(d.payment_method).toUpperCase())}</strong>
                 </div>`
              : ""
          }
          ${
            d.rental_id
              ? `<div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
                   <span style="color:#6B7280;">Rental</span>
                   <strong>#${escapeHtml(d.rental_id)}</strong>
                 </div>`
              : ""
          }
          ${
            d.payment_id
              ? `<div style="display:flex; justify-content:space-between; padding:6px 0;">
                   <span style="color:#6B7280;">Payment ID</span>
                   <strong>#${escapeHtml(d.payment_id)}</strong>
                 </div>`
              : ""
          }
          ${
            d.failure_reason
              ? `<div style="margin-top:12px; padding:10px 12px; background:#FEF2F2; border-radius:8px; color:#DC3545; font-size:13px;">
                   <strong>Failure reason:</strong> ${escapeHtml(d.failure_reason)}
                 </div>`
              : ""
          }
        </div>
      `,
      confirmButtonColor: Colors.PRIMARY,
      confirmButtonText: "Close",
      width: "460px",
    });
  };

  /* ── Show passenger detail (SweetAlert fallback) ── */
  const showPassengerDetail = (notification) => {
    const d = notification.data || {};

    MySwal.fire({
      title: "New Passenger",
      html: `
        <div style="text-align:left; font-size:14px; line-height:1.7;">
          <div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
            <span style="color:#6B7280;">Name</span>
            <strong>${escapeHtml(d.name || "Unknown")}</strong>
          </div>
          <div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
            <span style="color:#6B7280;">Email</span>
            <strong>${escapeHtml(d.email || "—")}</strong>
          </div>
          ${
            d.phone
              ? `<div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
                   <span style="color:#6B7280;">Phone</span>
                   <strong>${escapeHtml(d.phone)}</strong>
                 </div>`
              : ""
          }
          ${
            d.passenger_id
              ? `<div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
                   <span style="color:#6B7280;">Passenger ID</span>
                   <strong>#${escapeHtml(d.passenger_id)}</strong>
                 </div>`
              : ""
          }
          ${
            d.signup_via
              ? `<div style="display:flex; justify-content:space-between; padding:6px 0;">
                   <span style="color:#6B7280;">Signed up via</span>
                   <strong>${escapeHtml(d.signup_via)}</strong>
                 </div>`
              : ""
          }
        </div>
      `,
      confirmButtonColor: Colors.PRIMARY,
      confirmButtonText: "Close",
      width: "440px",
    });
  };

  /* ── Show operator detail (SweetAlert fallback) ── */
  const showOperatorDetail = (notification) => {
    const d = notification.data || {};

    MySwal.fire({
      title: escapeHtml(notification.title || "Operator Event"),
      html: `
        <div style="text-align:left; font-size:14px; line-height:1.7;">
          <p style="margin-bottom:12px;">${escapeHtml(notification.body)}</p>
          ${
            d.operator_id
              ? `<div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
                   <span style="color:#6B7280;">Operator ID</span>
                   <strong>#${escapeHtml(d.operator_id)}</strong>
                 </div>`
              : ""
          }
          ${
            d.name
              ? `<div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
                   <span style="color:#6B7280;">Name</span>
                   <strong>${escapeHtml(d.name)}</strong>
                 </div>`
              : ""
          }
          ${
            d.email
              ? `<div style="display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #E5E7EB;">
                   <span style="color:#6B7280;">Email</span>
                   <strong>${escapeHtml(d.email)}</strong>
                 </div>`
              : ""
          }
        </div>
      `,
      confirmButtonColor: Colors.PRIMARY,
      confirmButtonText: "Close",
      width: "440px",
    });
  };

  /* ── Row click ── */
  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    const type = notification.type;
    const data = notification.data || {};

    /* ── Rental request → parent modal (accept/reject) ── */
    if (type === "rental_request" && data.rental_id) {
      onOpenModal?.(true, "rental", { rental_id: data.rental_id });
      return;
    }

    /* ── Rental update → parent modal ── */
    if (type === "rental_update" && data.rental_id) {
      onOpenModal?.(true, "rental_update", data);
      return;
    }

    /* ── Payment events ── */
    if (
      (type === "payment_received" ||
        type === "payment_failed" ||
        type === "payment_admin" ||
        type === "payment_confirmation" ||
        type === "payment") &&
      data.payment_id
    ) {
      if (USE_PARENT_MODALS.payment && onOpenModal) {
        onOpenModal(true, "payment", {
          payment_id: data.payment_id,
          rental_id: data.rental_id,
        });
        return;
      }
      showPaymentDetail(notification);
      return;
    }

    /* ── Passenger registration ── */
    if (type === "passenger_registered" && data.passenger_id) {
      if (USE_PARENT_MODALS.passenger && onOpenModal) {
        onOpenModal(true, "passenger", {
          passenger_id: data.passenger_id,
        });
        return;
      }
      showPassengerDetail(notification);
      return;
    }

    /* ── Operator events ── */
    if (
      (type === "operator_registered" ||
        type === "operator_documents_uploaded" ||
        type === "operator_resubmitted") &&
      data.operator_id
    ) {
      if (USE_PARENT_MODALS.operator && onOpenModal) {
        onOpenModal(true, "operator", {
          operator_id: data.operator_id,
        });
        return;
      }
      showOperatorDetail(notification);
      return;
    }

    /* ── Default fallback ── */
    MySwal.fire({
      title: escapeHtml(notification.title),
      html: `
        <div style="text-align: left;">
          <p>${escapeHtml(notification.body)}</p>
          <p style="font-size:12px; color:#6B7280; margin-top:12px;">
            <strong>Type:</strong> ${escapeHtml(notification.type)}<br/>
            <strong>Date:</strong> ${new Date(
              notification.created_at,
            ).toLocaleString()}
          </p>
        </div>
      `,
      confirmButtonColor: Colors.PRIMARY,
      width: "500px",
    });
  };

  /* ── Icons ── */
  const getNotificationIcon = (type) => {
    switch (type) {
      case "rental_request":
        return "bi-calendar-plus";
      case "rental_update":
        return "bi-calendar-check";
      case "rental_cancelled":
        return "bi-calendar-x";
      case "payment":
        return "bi-credit-card";
      case "payment_received":
        return "bi-credit-card";
      case "payment_admin":
        return "bi-credit-card";
      case "payment_confirmation":
        return "bi-credit-card";
      case "payment_failed":
        return "bi-x-octagon";
      case "passenger_registered":
        return "bi-person-check";
      case "system":
        return "bi-info-circle";
      case "promotion":
        return "bi-tag";
      case "operator_registered":
        return "bi-person-plus";
      case "operator_documents_uploaded":
        return "bi-file-earmark-arrow-up";
      case "operator_resubmitted":
        return "bi-arrow-repeat";
      case "operator_approved":
        return "bi-shield-check"; // Added for new push
      case "operator_rejected":
        return "bi-shield-x"; // Added for new push
      default:
        return "bi-bell";
    }
  };

  /* ── Colors ── */
  const getNotificationColor = (type) => {
    switch (type) {
      case "rental_request":
        return Colors.PRIMARY;
      case "rental_update":
        return Colors.SUCCESS;
      case "rental_cancelled":
        return Colors.RED;
      case "payment":
        return Colors.WARNING;
      case "payment_received":
        return Colors.SUCCESS;
      case "payment_admin":
        return Colors.SUCCESS;
      case "payment_confirmation":
        return Colors.SUCCESS;
      case "payment_failed":
        return Colors.RED;
      case "passenger_registered":
        return Colors.SECONDARY_BLUE;
      case "system":
        return Colors.SECONDARY_BLUE;
      case "promotion":
        return Colors.INFO;
      case "operator_registered":
        return "#20c997";
      case "operator_documents_uploaded":
        return "#0dcaf0";
      case "operator_resubmitted":
        return "#fd7e14";
      case "operator_approved":
        return Colors.SUCCESS; // Added for new push
      case "operator_rejected":
        return Colors.RED; // Added for new push
      default:
        return Colors.PRIMARY;
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const diffInMs = Date.now() - date;
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      return diffInMinutes < 1 ? "Just now" : `${diffInMinutes}m ago`;
    }
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const loadMore = () => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNotifications(nextPage, false);
  };

  /* ── Report unread count up ── */
  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount]);

  /* ── Fetch on open / filter change ── */
  useEffect(() => {
    if (isOpen) {
      setPage(1);
      fetchNotifications(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filter]);

  /* ── Auto-refresh while open ── */
  useEffect(() => {
    if (!isOpen) return;

    const id = setInterval(() => {
      fetchNotifications(1, true);
    }, PANEL_REFRESH_MS);

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filter]);

  /* ── Click outside to close ── */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".swal2-container")) return;
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="notif-backdrop" />

      <div
        ref={notificationRef}
        className="notif-panel"
        style={{
          "--notif-text-dark": Colors.TEXT_DARK,
          "--notif-text-light": Colors.TEXT_LIGHT,
          "--notif-border": Colors.LIGHT_GRAY,
          "--notif-accent": Colors.PRIMARY,
          "--notif-danger": Colors.RED,
          "--notif-success": Colors.SUCCESS,
        }}
      >
        {/* Header */}
        <div className="notif-header">
          <div className="notif-header-top">
            <h5 className="notif-title">
              Notifications
              {unreadCount > 0 && (
                <span className="notif-unread-pill">{unreadCount}</span>
              )}
            </h5>
            <button
              className="notif-close-btn"
              onClick={onClose}
              aria-label="Close notifications"
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          <div className="notif-filters">
            {FILTERS.map(({ key, label }) => (
              <button
                key={key}
                className={`notif-filter-btn${filter === key ? " active" : ""}`}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="notif-actions">
            <button
              className="notif-action-btn"
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
            >
              <i className="bi bi-check2-all me-1" />
              Mark All Read
            </button>
          </div>
        </div>

        {/* List */}
        <div className="notif-list">
          {loading && notifications.length === 0 ? (
            <div className="notif-state">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="notif-state-text">Loading notifications...</p>
            </div>
          ) : error && notifications.length === 0 ? (
            <div className="notif-state">
              <i className="bi bi-exclamation-triangle notif-state-icon text-warning" />
              <p className="notif-state-text">{error}</p>
              <button
                className="notif-retry-btn"
                onClick={() => fetchNotifications(1, true)}
              >
                Retry
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="notif-state">
              <i className="bi bi-bell-slash notif-state-icon" />
              <p className="notif-state-text">
                {filter === "unread"
                  ? "No unread notifications"
                  : filter === "all"
                    ? "No notifications yet"
                    : `No ${(
                        FILTERS.find((f) => f.key === filter)?.label || filter
                      ).toLowerCase()} yet`}
              </p>
            </div>
          ) : (
            <>
              {notifications.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  color={getNotificationColor(notification.type)}
                  icon={getNotificationIcon(notification.type)}
                  timeAgo={formatTimeAgo(notification.created_at)}
                  acting={actingOnId === notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  onDelete={(e) => {
                    e.stopPropagation();
                    deleteNotification(notification.id);
                  }}
                  onAccept={(e) => {
                    e.stopPropagation();
                    respondToRental(notification, "accept");
                  }}
                  onReject={(e) => {
                    e.stopPropagation();
                    respondToRental(notification, "reject");
                  }}
                />
              ))}

              {hasMore && !loading && (
                <div className="notif-load-more">
                  <button className="notif-retry-btn" onClick={loadMore}>
                    Load More
                  </button>
                </div>
              )}

              {loading && notifications.length > 0 && (
                <div className="notif-load-more">
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

/* ────────────────────── Notification Row ────────────────────── */

const NotificationRow = ({
  notification,
  color,
  icon,
  timeAgo,
  acting,
  onClick,
  onDelete,
  onAccept,
  onReject,
}) => {
  const showRentalActions =
    notification.type === "rental_request" &&
    notification.data?.action_required &&
    !notification.data?.decision;

  return (
    <div
      className={`notif-row${!notification.is_read ? " unread" : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
    >
      <div
        className="notif-row-icon"
        style={{ backgroundColor: `${color}1A`, color }}
      >
        <i className={`bi ${icon}`} />
      </div>

      <div className="notif-row-body">
        <div className="notif-row-top">
          <h6 className="notif-row-title">
            {notification.title}
            {!notification.is_read && (
              <span className="notif-new-pill">New</span>
            )}
          </h6>
          <button
            className="notif-delete-btn"
            onClick={onDelete}
            aria-label="Delete notification"
          >
            <i className="bi bi-trash" />
          </button>
        </div>

        <p className="notif-row-message">{notification.body}</p>

        <div className="notif-row-bottom">
          <small className="notif-row-time">{timeAgo}</small>
          <span
            className="notif-type-badge"
            style={{ backgroundColor: `${color}26`, color }}
          >
            {notification.type.replace(/_/g, " ")}
          </span>
        </div>

        {showRentalActions && (
          <div className="notif-row-rental-actions">
            <button
              className="notif-accept-btn"
              onClick={onAccept}
              disabled={acting}
            >
              {acting ? <span className="notif-btn-spinner" /> : "Accept"}
            </button>
            <button
              className="notif-reject-btn"
              onClick={onReject}
              disabled={acting}
            >
              {acting ? <span className="notif-btn-spinner" /> : "Reject"}
            </button>
          </div>
        )}

        {notification.data?.decision && (
          <span
            className={`notif-decision-pill notif-decision-${notification.data.decision}`}
          >
            {notification.data.decision === "accept" ? "Accepted" : "Rejected"}
          </span>
        )}
      </div>
    </div>
  );
};

export default Notifications;
